import { FormEvent, useEffect, useState } from 'react';
import type { CompetitorCandidateDto, CompetitorsResponse } from '@swg/contracts';
import { CONFIDENCE_LABELS, type Confidence } from '@swg/domain';
import {
  addCompetitor,
  ApiError,
  confirmCompetitors,
  fetchCompetitors,
  removeCandidate,
  setCandidateSelection,
  setCompetitorKvk,
} from '../api.js';
import { sourceHost } from './Niche.js';
import { theme } from '../theme.js';

const LABEL_COLOR: Record<Confidence, string> = {
  low: '#C99A2E',
  medium: theme.color.primary,
  high: '#1E7A44',
};

export function CompetitorSelection({
  runId,
  runActive,
  canEdit,
  onChanged,
}: {
  runId: string;
  runActive: boolean;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [data, setData] = useState<CompetitorsResponse | null>(null);
  const [state, setState] = useState<'loading' | 'none' | 'ready'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchCompetitors(runId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setState(d.candidates.length > 0 || d.confirmed ? 'ready' : 'none');
      })
      .catch(() => !cancelled && setState('none'));
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (state === 'loading') return null;
  if (state === 'none') {
    return runActive ? (
      <p style={{ color: theme.color.muted, fontSize: 13.5 }}>Concurrenten worden voorgesteld…</p>
    ) : null;
  }
  if (!data) return null;

  const confirmed = data.confirmed;
  const editable = canEdit && !confirmed;
  const atLimit = data.selectedCount >= data.limit;

  async function run(action: () => Promise<CompetitorsResponse>) {
    setError(null);
    setBusy(true);
    try {
      setData(await action());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setBusy(false);
    }
  }

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    await run(() => addCompetitor(runId, { url: url.trim(), name: name.trim() || undefined }));
    setUrl('');
    setName('');
  }

  async function onConfirm() {
    setError(null);
    setBusy(true);
    try {
      setData(await confirmCompetitors(runId));
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bevestigen mislukt.');
    } finally {
      setBusy(false);
    }
  }

  const visible = data.candidates.filter((c) => c.status !== 'REJECTED');
  const shown = confirmed ? visible.filter((c) => c.status === 'SELECTED') : visible;

  return (
    <div style={{ background: theme.color.surface, border: `1px solid ${theme.color.border}`, borderRadius: 14, padding: '22px 26px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Selecteer concurrenten</h2>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: confirmed ? '#1E7A44' : theme.color.primary }}>
          {confirmed ? `Bevestigd — ${shown.length} concurrenten` : `${data.selectedCount} van ${data.limit} geselecteerd`}
        </span>
      </div>
      <p style={{ margin: '0 0 18px', fontSize: 12.5, color: theme.color.muted, lineHeight: 1.5 }}>
        Automatisch voorgesteld op basis van je profiel. Kies er maximaal {data.limit}. Bronnen en
        matchende zoekwoorden staan per kandidaat vermeld.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((c) => (
          <CandidateCard
            key={c.id}
            c={c}
            editable={editable}
            atLimit={atLimit}
            busy={busy}
            onToggle={() => run(() => setCandidateSelection(runId, c.id, c.status !== 'SELECTED'))}
            onRemove={() => run(() => removeCandidate(runId, c.id))}
            onSaveKvk={(value) => run(() => setCompetitorKvk(runId, c.id, value))}
          />
        ))}
      </div>

      {editable && (
        <form onSubmit={onAdd} style={{ marginTop: 18, borderTop: `1px solid ${theme.color.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Concurrent handmatig toevoegen</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="domein of URL"
              required
              style={{ flex: '2 1 200px', padding: '9px 11px', fontSize: 13.5, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, fontFamily: 'inherit' }}
            />
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="naam (optioneel)"
              style={{ flex: '1 1 140px', padding: '9px 11px', fontSize: 13.5, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, fontFamily: 'inherit' }}
            />
            <button type="submit" disabled={busy} style={{ background: 'transparent', border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Toevoegen
            </button>
          </div>
        </form>
      )}

      {error && (
        <div style={{ background: '#FBE6E6', border: '1px solid #EFC2C2', color: '#A82A2A', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 14 }}>
          {error}
        </div>
      )}

      {editable && (
        <div style={{ marginTop: 18 }}>
          <button
            onClick={() => void onConfirm()}
            disabled={busy || data.selectedCount < 1}
            style={{ background: theme.color.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: busy || data.selectedCount < 1 ? 'default' : 'pointer', opacity: busy || data.selectedCount < 1 ? 0.6 : 1, fontFamily: 'inherit' }}
          >
            Selectie bevestigen
          </button>
          {data.selectedCount < 1 && (
            <span style={{ marginLeft: 12, fontSize: 12.5, color: theme.color.muted }}>Selecteer minstens één concurrent.</span>
          )}
        </div>
      )}
    </div>
  );
}

function CandidateCard({
  c,
  editable,
  atLimit,
  busy,
  onToggle,
  onRemove,
  onSaveKvk,
}: {
  c: CompetitorCandidateDto;
  editable: boolean;
  atLimit: boolean;
  busy: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onSaveKvk: (value: string) => void;
}) {
  const selected = c.status === 'SELECTED';
  const disableSelect = busy || (!selected && atLimit);
  const [kvk, setKvk] = useState(c.kvkNumber ?? '');
  const kvkDirty = kvk !== (c.kvkNumber ?? '');
  return (
    <div style={{ border: `1px solid ${selected ? theme.color.primary : theme.color.border}`, borderRadius: 12, padding: '14px 16px', background: selected ? '#F5FAFD' : theme.color.surface }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>{c.name}</span>
            {c.origin === 'MANUAL' && <span style={{ fontSize: 11, color: theme.color.primary, fontWeight: 600 }}>handmatig</span>}
            {/* P25 — "Verifieer eerst, deel daarna pas in." Het woordoordeel
                (hoog/gemiddeld/laag) en de kleurstip zijn een indeling, en die
                hoort pas te verschijnen als er een bevestigd procespakket is.
                Zonder verificatie blijft de kale overlapscore staan: dat is een
                meting en geen oordeel, en hem helemaal weglaten zou informatie
                verbergen die de analist nodig heeft om te kiezen. */}
            {c.verifiedProcesses.length > 0 ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: theme.color.muted }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: LABEL_COLOR[c.relevanceLabel], display: 'inline-block' }} />
                {CONFIDENCE_LABELS[c.relevanceLabel]} ({c.relevanceScore.toFixed(2)})
              </span>
            ) : (
              <span
                style={{ fontSize: 11.5, color: theme.color.subtle }}
                title="Geen indeling zonder bevestigd procespakket; dit is alleen de gemeten overlap."
              >
                overlap {c.relevanceScore.toFixed(2)}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: theme.color.muted, marginTop: 2 }}>{c.domain}</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>{c.reason}</div>
          {c.matchedKeywords.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {c.matchedKeywords.map((k) => (
                <span key={k} style={{ fontSize: 11.5, background: theme.color.accentBg, color: theme.color.primary, borderRadius: 6, padding: '2px 8px' }}>{k}</span>
              ))}
            </div>
          )}
          {/* P22 — bevestigde procesoverlap. Leeg is nadrukkelijk "niet
              gecontroleerd" en niet "levert dit niet": alleen het longlijstkanaal
              verifieert, dus een kandidaat uit SE Ranking heeft hier niets staan
              zonder dat dat iets over hem zegt. Zonder dat onderscheid leest een
              lege regel als een oordeel dat we niet hebben geveld. */}
          {c.verifiedProcesses.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11.5, color: theme.color.ok, fontWeight: 600, marginBottom: 4 }}>
                Procespakket bevestigd op eigen bron
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {c.verifiedProcesses.map((v, i) => (
                  <a
                    key={`${v.process}-${i}`}
                    href={v.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={`bron: ${sourceHost(v.sourceUrl)}`}
                    style={{ fontSize: 11.5, background: '#E4F3EB', color: theme.color.ok, borderRadius: 6, padding: '2px 8px', textDecoration: 'none' }}
                  >
                    {v.process}
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 8 }}>
              Procespakket niet gecontroleerd — deze kandidaat komt niet uit de geverifieerde
              longlijst.
            </div>
          )}
          <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 8 }}>Bron: {c.source}</div>
          {editable ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, color: theme.color.muted }}>KVK-nummer:</span>
              <input
                inputMode="numeric"
                placeholder="8 cijfers"
                value={kvk}
                onChange={(e) => setKvk(e.target.value.replace(/\D/g, '').slice(0, 8))}
                style={{ width: 110, padding: '4px 8px', fontSize: 12.5, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 6, fontFamily: 'inherit' }}
              />
              {kvkDirty && (
                <button
                  onClick={() => onSaveKvk(kvk)}
                  disabled={busy || (kvk !== '' && kvk.length !== 8)}
                  style={{ background: 'transparent', border: `1px solid ${theme.color.borderStrong}`, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Bewaren
                </button>
              )}
            </div>
          ) : (
            c.kvkNumber && (
              <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 4 }}>KVK: {c.kvkNumber}</div>
            )
          )}
          {c.uncertainty && (
            <div style={{ fontSize: 11.5, color: theme.color.warnText, background: theme.color.warnBg, border: `1px solid ${theme.color.warnBorder}`, borderRadius: 6, padding: '4px 8px', marginTop: 8, display: 'inline-block' }}>
              {c.uncertainty}
            </div>
          )}
        </div>
        {editable && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <button
              onClick={onToggle}
              disabled={disableSelect}
              title={disableSelect && !selected ? 'Maximum bereikt' : undefined}
              style={{ background: selected ? theme.color.primary : 'transparent', color: selected ? '#fff' : theme.color.text, border: `1px solid ${selected ? theme.color.primary : theme.color.borderStrong}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: disableSelect ? 'default' : 'pointer', opacity: disableSelect ? 0.55 : 1, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
            >
              {selected ? 'Geselecteerd ✓' : 'Selecteren'}
            </button>
            <button onClick={onRemove} disabled={busy} style={{ background: 'transparent', border: 'none', color: theme.color.subtle, fontSize: 12, cursor: 'pointer' }}>
              Verwijderen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
